import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory, useLocation } from "react-router-dom";
import moment from "moment";
import cn from "clsx";
import has from "lodash/has";

import { useAppDispatch, useAppSelector } from "@/redux/store";
import { hidePageLoader, showPageLoader } from "@/redux/actions/common.action";
import { resetAPIError } from "@/redux/actions/homepage.action";
import { getOrderStatusData } from "@/redux/actions/order.action";
import {
  clearTrackOrder,
  trackOrder,
  trackOrderResponse
} from "@/redux/actions/myAccount.action";
import { useLongLanguage, useIsRTL, useRouteSlug } from "@/hooks";
import AccountAnalyticsEvent from "@/services/analytics/main/myAccount";
import { checkIsOrderHandedToShipper, getTrackOrderWebUrl } from "@/util/order";
import { HELP_DATA_TILES, LOADER_TYPES } from "@/constants";
import Page from "@/components/page";
import RoundImg from "@/components/roundImg";
import Breadcrumbs from "@/components/breadcrumbs";
import GuestTrackingOrder from "@/components/guestTrackingOrder";
import TrackOrderTimelines from "@/components/trackOrderTimelines";
import NeedHelpFooter from "../help/footer";
import "./style.scss";

const TrackOrder = () => {
  const [orderId, setOrderId] = useState("");
  const [isOrderHandedToShipper, setIsOrderHandedToShipper] = useState(false);
  const [email, setEmail] = useState("");
  const [showContent, setShowContent] = useState(false);

  const { t } = useTranslation(["trackOrder", "seo", "breadcrumbs"]);
  const isRTL = useIsRTL();
  const language = useLongLanguage();
  const location = useLocation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const routeSlug = useRouteSlug();

  const trackOrderData = useAppSelector(
    state => state.myAccountReducer.trackOrder
  );
  const userInfo = useAppSelector(state => state.authReducer);
  const cdnDomain = useAppSelector(
    state => state.common.configSettings.cdnDomain
  );
  const orderData = useAppSelector(state => state.orderReducer.order);

  const breadcrumbsList = [
    {
      defaultValue: t("breadcrumbs:home", { lng: "en" }),
      link: "/",
      value: t("breadcrumbs:home")
    },
    {
      defaultValue: t("trackShipment", { lng: "en" }),
      link: "/",
      value: t("trackShipment")
    }
  ];

  useEffect(() => {
    const token = location.search.replace("?token=", "");

    dispatch(getOrderStatusData(token, language)).then(response => {
      const orderIdResponse =
        response && response.data.success && response.data.orderId.trim();
      if (response.data.success) {
        trackOrderResponse(response.data);
      }
      if (orderIdResponse) {
        setOrderId(orderIdResponse);
      }
    });
    return () => {
      dispatch(clearTrackOrder());
      dispatch(resetAPIError());
    };
  }, []);

  useEffect(() => {
    if (userInfo && userInfo.accessToken) {
      const urlByToken = location.search.includes("token=")
        ? `/track-order/${location.search}`
        : "/my-account/my-orders/";

      const resultUrl = `/${routeSlug}${urlByToken}`;

      history.push(resultUrl);
    }
  }, [userInfo, userInfo?.accessToken]);

  const handleTextChange = event => {
    if (event.target.name === "orderId") {
      setOrderId(event.target.value.trim());

      if (Object.keys(trackOrderData).length > 0) {
        dispatch(clearTrackOrder());
        setShowContent(false);
      }
    }

    if (event.target.name === "email") {
      setEmail(event.target.value.trim());

      if (Object.keys(trackOrderData).length > 0) {
        dispatch(clearTrackOrder());
        setShowContent(false);
      }
    }
  };
  const handleOrderHandedStatus = trackOrderDetails => {
    const trackOrderWebUrl = getTrackOrderWebUrl({
      trackOrderWebUrl: cdnDomain?.trackOrderWebUrl,
      trackOrderDetails,
      language
    });
    window.open(trackOrderWebUrl);
  };

  const handleOnClickTrackOrder = async () => {
    dispatch(showPageLoader({ type: LOADER_TYPES.DOTS, fullscreen: true }));

    const { data } = await dispatch(
      trackOrder({
        email,
        orderId
      })
    );
    if (data.success) {
      AccountAnalyticsEvent.trackOrder({
        orderId
      });
    }
    const isOrderHandedToShipperStatus = checkIsOrderHandedToShipper(
      data?.orderStatusDetails
    );
    if (isOrderHandedToShipperStatus) {
      handleOrderHandedStatus(data);
    }
    dispatch(hidePageLoader({ type: LOADER_TYPES.DOTS, fullscreen: true }));
    setShowContent(true);
    setIsOrderHandedToShipper(isOrderHandedToShipperStatus);
  };

  const isTimeLineAvailable =
    trackOrderData.orderId?.includes(orderId) && !isOrderHandedToShipper;

  const isOrderCancelled =
    has(orderData, "items") &&
    orderData.items.some(
      item => item.quantity === 0 && item.cancelQty > item.quantity
    );
  return (
    <Page title={t("se:trackOrderTitle")}>
      <div className={cn("track_order_container", { arabic: isRTL })}>
        <div className="container large-container">
          <Breadcrumbs breadcrumbs={breadcrumbsList} />
        </div>
        <div className="container track_heading">
          {!(
            trackOrderData.orderId && trackOrderData.orderId.includes(orderId)
          ) ? (
            <div className="page_heading">
              <h2>{t("tractYourOrder")}</h2>
              {userInfo && !userInfo.userLoggedIn ? (
                <p>
                  {t("alreadyHaveAnAccount")}
                  <Link to={`/${routeSlug}/authorization/`}>
                    <button className="round_btn form_black_btn width_auto">
                      {t("signIn")}
                    </button>
                  </Link>
                </p>
              ) : null}
            </div>
          ) : null}
          <GuestTrackingOrder
            handleTextChange={handleTextChange}
            handleOnClickTrackOrder={handleOnClickTrackOrder}
            orderId={orderId}
            email={email}
            trackOrderGuest={trackOrderData.orderId}
            showContent={showContent}
            trackOrderR={trackOrderData}
          />
        </div>
        {isTimeLineAvailable ? (
          <div className="guest_order_wrapper container padding-web-0">
            <div className="product_order_timeline">
              <TrackOrderTimelines trackOrder={trackOrderData} />
              {isOrderCancelled && (
                <div className="cancel_orders">{t("orderCancelled")}</div>
              )}
            </div>
            <div className="product_order_detail">
              <div className="product_order_data">
                <div className="product_img">
                  {orderData?.items?.length > 1 ? (
                    <div>
                      <RoundImg
                        src={`${orderData.items[0].image}`}
                        alt={
                          orderData.items[0].title?.defaultValue ??
                          "Ordered product"
                        }
                        className={`initialProduct`}
                      />
                      <RoundImg
                        src={`${orderData.items[1].image}`}
                        alt={
                          orderData.items[1].title?.defaultValue ??
                          "Ordered product"
                        }
                        withOverlay={true}
                        productCount={orderData.items.length - 2}
                      />
                    </div>
                  ) : (
                    <div>
                      <RoundImg
                        src={`${orderData?.items?.[0]?.image}`}
                        alt={
                          orderData.items[0].title?.defaultValue ??
                          "Ordered product"
                        }
                        className={`initialProduct`}
                      />
                    </div>
                  )}
                </div>
                <div className="order_id">
                  <h4>
                    <span>{t("orderId")}:</span> {trackOrderData.orderId}
                  </h4>
                </div>
                <div className="order_placed">
                  <h4>{t("orderPlacedOn")}:</h4>
                  <p>
                    {moment.utc(trackOrderData.orderPlacedDate).format("LLL")}
                  </p>
                </div>
                {Boolean(trackOrderData.trackingNumber) && (
                  <div className="order_tracking_number">
                    <h4>{t("trackNumber")}:</h4>
                    <p>{trackOrderData.trackingNumber}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}
        <NeedHelpFooter helpDataTiles={HELP_DATA_TILES} />
      </div>
    </Page>
  );
};

export default TrackOrder;
